import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useLocalizedMethods } from "@/hooks/useLocalizedMethods";
import { Upload, LogOut, CreditCard, Sparkles, Trash2, User as UserIcon } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import { BottomNav, Tab } from "@/components/BottomNav";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AppLayout } from "@/components/AppLayout";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";


export default function Profile() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const { localizeMethods } = useLocalizedMethods();
  const { subscribed, isTrialActive, trialDaysRemaining, subscriptionEnd, cancelAtPeriodEnd, cancelAt, openCustomerPortal, checkSubscription, user, loading: subscriptionLoading, createCheckout, stripeCustomerId } = useSubscription();
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteWarning, setDeleteWarning] = useState<string | null>(null);
  const [deleteRequiresCancellation, setDeleteRequiresCancellation] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>("profile");
  const [profileData, setProfileData] = useState<any>(null);
  const [rawMethods, setRawMethods] = useState<any[]>([]);

  const [name, setName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [email, setEmail] = useState("");

  const methods = localizeMethods(rawMethods);

  // Dummy handlers for sidebar - just navigate to chat
  const handleNewConversation = async () => {
    navigate("/chat");
  };

  const handleSelectConversation = async (id: string) => {
    navigate("/chat");
  };

  const handleDeleteConversation = async (id: string) => {
    // Not used on profile page
  };

  const handleRenameConversation = async (id: string, title: string) => {
    // Not used on profile page
  };

  const handleMethodClick = (method: any) => {
    navigate("/chat", { state: { selectedMethod: method } });
  };

  useEffect(() => {
    loadProfile();
  }, []);

  // Refresh subscription only when user is loaded
  useEffect(() => {
    if (user && !subscriptionLoading) {
      checkSubscription();
    }
  }, [user, subscriptionLoading]);

  const loadProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/auth");
        return;
      }

      setEmail(user.email || "");

      // Load profile and methods in parallel
      const [profileResult, methodsResult] = await Promise.all([
        supabase.from("profiles").select("*").eq("id", user.id).maybeSingle(),
        supabase.from("training_methods").select("*").order("category")
      ]);

      if (profileResult.data) {
        setName(profileResult.data.name || "");
        setAvatarUrl(profileResult.data.avatar_url || "");
        setProfileData(profileResult.data);
      }

      if (methodsResult.data) {
        setRawMethods(methodsResult.data);
      }
    } catch (error) {
      console.error("Error loading profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setUpdating(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: { session } } = await supabase.auth.getSession();
      if (!user || !session) return;

      // Update profile in database
      const { error } = await supabase
        .from("profiles")
        .update({
          name,
        })
        .eq("id", user.id);

      if (error) throw error;

      // Sync name to Stripe
      try {
        await supabase.functions.invoke('update-stripe-customer', {
          body: { name },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });
      } catch (stripeError) {
        console.warn("Failed to sync name to Stripe:", stripeError);
        // Don't fail the whole update if Stripe sync fails
      }

      toast({
        title: t('profile.saveSuccess'),
        description: t('profile.saveSuccess'),
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: t('profile.saveError'),
        description: t('profile.saveError'),
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        variant: "destructive",
        title: t('profile.saveError'),
        description: "Das Bild darf maximal 2MB groß sein.",
      });
      return;
    }

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast({
        variant: "destructive",
        title: t('profile.saveError'),
        description: "Bitte wähle ein Bild aus.",
      });
      return;
    }

    setUploading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const fileExt = file.name.split(".").pop();
      const filePath = `${user.id}/avatar.${fileExt}`;

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data } = supabase.storage
        .from("avatars")
        .getPublicUrl(filePath);

      // Update profile with avatar URL
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: data.publicUrl })
        .eq("id", user.id);

      if (updateError) throw updateError;

      setAvatarUrl(data.publicUrl);
      toast({
        title: t('profile.saveSuccess'),
        description: "Dein Profilbild wurde aktualisiert.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: t('profile.saveError'),
        description: "Das Bild konnte nicht hochgeladen werden.",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const handleDeleteAccount = async () => {
    // First check current subscription status
    setDeleting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Call the delete-account edge function (without confirmed flag)
      const { data, error } = await supabase.functions.invoke('delete-account', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      
      // If there's an error, we need to fetch the actual response body
      if (error) {
        // For FunctionsHttpError, we need to make a raw fetch to get the body
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (!currentSession) return;
        
        const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-account`;
        const rawResponse = await fetch(functionUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${currentSession.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            'Content-Type': 'application/json',
          },
        });
        
        const responseData = await rawResponse.json();
        console.log('Raw response data:', responseData);
        
        if (responseData.requiresCancellation) {
          setDeleteWarning(responseData.error);
          setDeleteRequiresCancellation(true);
          setShowDeleteConfirmation(true);
          return;
        }
        
        if (responseData.requiresConfirmation) {
          setDeleteWarning(responseData.warning);
          setDeleteRequiresCancellation(false);
          setShowDeleteConfirmation(true);
          return;
        }
        
        toast({
          variant: "destructive",
          title: t('profile.saveError'),
          description: responseData.error || "Profil konnte nicht gelöscht werden. Bitte kontaktiere den Support.",
        });
        return;
      }
      
      // Check if we got a warning about active subscription that's being cancelled
      if (data?.requiresConfirmation) {
        setDeleteWarning(data.warning);
        setDeleteRequiresCancellation(false);
        setShowDeleteConfirmation(true);
        return;
      }
      
      // Success - no active subscription, proceed with deletion
      toast({
        title: "Profil gelöscht",
        description: "Dein Profil wurde erfolgreich gelöscht.",
      });
      
      // Sign out and redirect
      await supabase.auth.signOut();
      navigate("/");
    } catch (error) {
      toast({
        variant: "destructive",
        title: t('profile.saveError'),
        description: "Profil konnte nicht gelöscht werden.",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleConfirmDelete = async () => {
    setShowDeleteConfirmation(false);
    
    // Actually delete the account with confirmation
    setDeleting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      
      // Call delete-account with confirmed flag
      const { data, error: deleteError } = await supabase.functions.invoke('delete-account', {
        body: { confirmed: true },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      
      if (deleteError) {
        toast({
          variant: "destructive",
          title: t('profile.saveError'),
          description: "Profil konnte nicht gelöscht werden.",
        });
        return;
      }
      
      toast({
        title: "Profil gelöscht",
        description: "Dein Profil wurde erfolgreich gelöscht.",
      });
      
      await supabase.auth.signOut();
      navigate("/");
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Profil konnte nicht gelöscht werden.",
      });
    } finally {
      setDeleting(false);
    }
  };


  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    if (tab === "chat") {
      navigate("/chat");
    } else if (tab === "loop") {
      navigate("/loop");
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('profile.loading', 'Lädt Profil...')}</p>
        </div>
      </div>
    );
  }

  // Mobile view
  if (isMobile) {
    return (
      <>
        <div className="flex flex-col h-screen bg-background">
          {/* Header */}
          <div className="sticky top-0 z-40 flex items-center justify-between px-4 py-3 bg-card border-b border-border">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <h1 className="text-lg font-semibold">{t('nav.profile')}</h1>
            </div>
            <div className="flex items-center gap-2">
              <LanguageSwitcher />
              <Avatar className="h-8 w-8">
                <AvatarImage src={avatarUrl} />
                <AvatarFallback>
                  <UserIcon className="h-4 w-4" />
                </AvatarFallback>
              </Avatar>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 pb-20">
            <div className="max-w-2xl mx-auto space-y-6">
              <ProfileContent
                t={t}
                name={name}
                setName={setName}
                avatarUrl={avatarUrl}
                email={email}
                updating={updating}
                uploading={uploading}
                subscribed={subscribed}
                isTrialActive={isTrialActive}
                trialDaysRemaining={trialDaysRemaining}
                subscriptionEnd={subscriptionEnd}
                cancelAtPeriodEnd={cancelAtPeriodEnd}
                stripeCustomerId={stripeCustomerId}
                openCustomerPortal={openCustomerPortal}
                createCheckout={createCheckout}
                handleUpdateProfile={handleUpdateProfile}
                handleAvatarUpload={handleAvatarUpload}
                handleLogout={handleLogout}
                handleDeleteAccount={handleDeleteAccount}
                deleting={deleting}
                deleteWarning={deleteWarning}
                deleteRequiresCancellation={deleteRequiresCancellation}
                showDeleteConfirmation={showDeleteConfirmation}
                setShowDeleteConfirmation={setShowDeleteConfirmation}
                handleConfirmDelete={handleConfirmDelete}
              />
            </div>
          </div>
        </div>
        <BottomNav activeTab={activeTab} onTabChange={handleTabChange} />
      </>
    );
  }

  // Desktop view with sidebar
  return (
    <AppLayout 
      activeView="chat" 
      profile={profileData}
      conversations={[]}
      currentConversationId={null}
      onSelectConversation={handleSelectConversation}
      onNewConversation={handleNewConversation}
      onDeleteConversation={handleDeleteConversation}
      onRenameConversation={handleRenameConversation}
      methods={methods}
      onMethodClick={handleMethodClick}
    >
      <div className="flex-1 overflow-y-auto bg-background p-4">
            <div className="max-w-2xl mx-auto space-y-6">
              <ProfileContent
                t={t}
                name={name}
                setName={setName}
                avatarUrl={avatarUrl}
                email={email}
                updating={updating}
                uploading={uploading}
                subscribed={subscribed}
                isTrialActive={isTrialActive}
                trialDaysRemaining={trialDaysRemaining}
                subscriptionEnd={subscriptionEnd}
                cancelAtPeriodEnd={cancelAtPeriodEnd}
                stripeCustomerId={stripeCustomerId}
                openCustomerPortal={openCustomerPortal}
                createCheckout={createCheckout}
                handleUpdateProfile={handleUpdateProfile}
                handleAvatarUpload={handleAvatarUpload}
                handleLogout={handleLogout}
                handleDeleteAccount={handleDeleteAccount}
                deleting={deleting}
                deleteWarning={deleteWarning}
                deleteRequiresCancellation={deleteRequiresCancellation}
                showDeleteConfirmation={showDeleteConfirmation}
                setShowDeleteConfirmation={setShowDeleteConfirmation}
                handleConfirmDelete={handleConfirmDelete}
              />
            </div>
          </div>
    </AppLayout>
  );
}

const ProfileContent = ({
  t,
  name,
  setName,
  avatarUrl,
  email,
  updating,
  uploading,
  subscribed,
  isTrialActive,
  trialDaysRemaining,
  subscriptionEnd,
  cancelAtPeriodEnd,
  stripeCustomerId,
  openCustomerPortal,
  createCheckout,
  handleUpdateProfile,
  handleAvatarUpload,
  handleLogout,
  handleDeleteAccount,
  deleting,
  deleteWarning,
  deleteRequiresCancellation,
  showDeleteConfirmation,
  setShowDeleteConfirmation,
  handleConfirmDelete,
}: any) => {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{t('profile.title')}</CardTitle>
            <CardDescription>{t('profile.subtitle', 'Verwalte deine Profil-Informationen')}</CardDescription>
          </div>
          <LanguageSwitcher />
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
            {/* Avatar Upload */}
            <div className="flex flex-col items-center gap-4">
              <Avatar className="w-24 h-24">
                <AvatarImage src={avatarUrl} />
                <AvatarFallback className="text-2xl">
                  {name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <Label htmlFor="avatar-upload" className="cursor-pointer">
                  <Button variant="outline" size="sm" disabled={uploading} asChild>
                    <span>
                      <Upload className="w-4 h-4 mr-2" />
                      {uploading ? t('profile.uploading', 'Wird hochgeladen...') : t('profile.uploadAvatar', 'Avatar hochladen')}
                    </span>
                  </Button>
                  <Input
                    id="avatar-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarUpload}
                    disabled={uploading}
                  />
                </Label>
              </div>
            </div>

            {/* Profile Form */}
            <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('profile.email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                disabled
                className="bg-muted"
              />
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">{t('profile.name')} *</Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  disabled={updating}
                />
              </div>

              <Button type="submit" className="w-full" disabled={updating}>
                {updating ? t('profile.saving', 'Wird gespeichert...') : t('profile.save')}
              </Button>
            </form>

            {/* Subscription Status */}
            <div className="space-y-4">
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">{t('profile.subscriptionStatus', 'Abonnement-Status')}</h3>
                
                {isTrialActive && trialDaysRemaining > 0 ? (
                  <div className="space-y-3">
                    <div className="p-3 bg-primary/10 rounded-lg">
                      <p className="font-medium text-primary">🎯 {t('subscription.trialActive', 'Testphase aktiv')}</p>
                      <p className="text-sm text-muted-foreground">
                        {t('subscription.trialRemaining', 'Noch {{days}} {{unit}} unbegrenzter Zugriff', {
                          days: trialDaysRemaining,
                          unit: trialDaysRemaining === 1 ? t('subscription.day', 'Tag') : t('subscription.days', 'Tage')
                        })}
                      </p>
                    </div>
                    {stripeCustomerId ? (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={openCustomerPortal}
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        {t('subscription.manage', 'Abonnement verwalten')}
                      </Button>
                    ) : (
                      <Button
                        variant="default"
                        className="w-full"
                        onClick={createCheckout}
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        {t('subscription.upgradeNow', 'Jetzt upgraden')}
                      </Button>
                    )}
                  </div>
                ) : subscribed ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-primary/10 rounded-lg">
                      <div>
                        {cancelAtPeriodEnd ? (
                          <>
                            <p className="font-medium text-primary">⚠️ {t('subscription.cancellingStatus', 'Aktiv (läuft ab)')}</p>
                            {subscriptionEnd && (
                              <p className="text-sm text-muted-foreground">
                                {t('subscription.endsOn', 'Endet am')}: {new Date(subscriptionEnd).toLocaleDateString(t('profile.locale', 'de-DE'))}
                              </p>
                            )}
                          </>
                        ) : (
                          <>
                            <p className="font-medium text-primary">✓ {t('subscription.premiumActive', 'Premium Aktiv')}</p>
                            {subscriptionEnd && (
                              <p className="text-sm text-muted-foreground">
                                {t('subscription.renewsOn', 'Verlängert sich am')}: {new Date(subscriptionEnd).toLocaleDateString(t('profile.locale', 'de-DE'))}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={openCustomerPortal}
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      {t('subscription.manage', 'Abonnement verwalten')}
                    </Button>
                  </div>
                ) : (
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      {t('subscription.noActive', 'Kein aktives Abonnement')}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Logout Button */}
            <Button
              variant="outline"
              className="w-full"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              {t('profile.logout')}
            </Button>

            {/* Delete Account */}
            <Button
              variant="destructive"
              className="w-full"
              disabled={deleting}
              onClick={handleDeleteAccount}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {deleting ? "Wird geprüft..." : "Profil löschen"}
            </Button>
            
            <AlertDialog open={showDeleteConfirmation} onOpenChange={setShowDeleteConfirmation}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {deleteRequiresCancellation ? "⚠️ Abonnement muss zuerst gekündigt werden" : "⚠️ Abonnement noch aktiv"}
                  </AlertDialogTitle>
                  <AlertDialogDescription className="space-y-2">
                    <p>{deleteWarning}</p>
                    {!deleteRequiresCancellation && (
                      <p className="font-semibold text-foreground">
                        Diese Aktion kann nicht rückgängig gemacht werden. Dein Profil und alle zugehörigen Daten werden dauerhaft gelöscht.
                      </p>
                    )}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>
                    {deleteRequiresCancellation ? "Verstanden" : "Abbrechen"}
                  </AlertDialogCancel>
                  {!deleteRequiresCancellation && (
                    <AlertDialogAction
                      onClick={handleConfirmDelete}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {deleting ? "Wird gelöscht..." : "Trotzdem löschen"}
                    </AlertDialogAction>
                  )}
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
      </CardContent>
    </Card>
  );
};
